import {
  Button,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import React, { Fragment, useContext, useEffect, useState } from "react";
import propTypes from "prop-types";
import SearchIcon from "@mui/icons-material/Search";

import { PagedDataView } from "../../../components/lists/PagedDataView/PagedDataView";
import TabbedView from "../../../components/lists/TabbedView/TabbedView";
import TabbedViewPage from "../../../components/lists/TabbedView/TabbedViewPage";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import PimTranListItem from "../../../components/lists/listItems/PimTranListItem/PimTranListItem";
import DateField from "../../../components/editable/EditableField/fields/DateField";
import TimeField from "../../../components/editable/EditableField/fields/TimeField";

import {
  api,
  IListPim,
  ListPimType,
  PimTransaction,
  IReproccessPim,
} from "../../../logic/api";

import flex from "../../../styles/flex";

import { SessionContext } from "../../../utility/useSession";
import { useDate } from "../../../utility/useDate";
import { useTranslation } from "react-i18next";

const PimTransactionsView: React.FunctionComponent<{
  clientCode?: string;
  bookingId?: number;
}> = (props) => {
  const session = useContext(SessionContext);
  const retryTransactionRequest = api.hooks.admin.reprocessPimTransaction();
  const [filter, setFilter] = useState("");
  const [retryQueue, setRetryQueue] = useState<IReproccessPim[]>([]);
  const { localizeDate } = useDate();
  const { t } = useTranslation();

  const startDateDefault = new Date();
  startDateDefault.setHours(0);
  startDateDefault.setMinutes(0);
  startDateDefault.setSeconds(0);
  startDateDefault.setMilliseconds(0);

  const endDateDefault = new Date();
  endDateDefault.setHours(23);
  endDateDefault.setMinutes(59);
  endDateDefault.setSeconds(59);
  endDateDefault.setMilliseconds(999);

  const [startDate, setStartDate] = useState(localizeDate(startDateDefault).toISO() ?? "");
  const [endDate, setEndDate] = useState(localizeDate(endDateDefault).toISO() ?? "");

  const [
    listPimParams,
    setListPimParams,
  ] = useState<IListPim>({
    type: ListPimType.all,
    clientCode: props.clientCode,
    startDate: startDate,
    endDate: endDate,
    bookingId: props.bookingId,
  });

  const retryTransactions = () => {
    const transactions = [...retryQueue];
    const retryNext = () => {
      const transaction = transactions[0];
      if (transaction == null) {
        setRetryQueue([]);
        setListPimParams({ ...listPimParams, type: 0 });
        return;
      }
      transactions.splice(0, 1);
      retryTransactionRequest.send(
        session.current.context,
        {
          pimId: transaction.pimId,
        },
        (result) => {
          if (result != null) {
            retryNext();
          }
        }
      );
    };
    retryNext();
  };

  useEffect(() => {
    setListPimParams({
      ...listPimParams, bookingId: props.bookingId
    });
  }, [props.bookingId]);

  return (
    <Fragment>
      <ProgressDialog status={retryTransactionRequest.status} />
      <ErrorDialog err={retryTransactionRequest.err} />
      <div style={flex.flexRows}>
        <div style={flex.flexFill}>
          <div style={{ textAlign: "left" }}>
            <div style={{ display: "inline-block" }}>
              <TabbedView
                selectedIndex={listPimParams.type}
                onSelectedIndexChange={(index) => {
                  setListPimParams({
                    ...listPimParams,
                    type: index,
                  });
                }}
              >
                <TabbedViewPage title={t("general.all")} />
                <TabbedViewPage title={t("mobile.errors")} />
                <TabbedViewPage title={t("mobile.processed")} />

              </TabbedView>
            </div>
          </div>
        </div>

        {
          props.bookingId == null && (
            <div style={flex.flexAuto}>
              <TextField
                autoFocus={false}
                label={t("home.search.searchFieldLabel")}
                variant="outlined"
                size="small"
                type="search"
                value={filter}
                onChange={(e) => {
                  setFilter(e.currentTarget.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.currentTarget) {
                      e.currentTarget.blur();
                      setListPimParams({
                        ...listPimParams,
                        filter: filter,
                      });
                    }
                    return false;
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position={"end"}>
                      <IconButton
                        style={{ padding: 0 }}
                        onClick={() => {
                          setListPimParams({
                            ...listPimParams,
                            filter: filter,
                          });
                        }}
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )
        }
        <div style={flex.flexAuto}>
          <div style={{ marginLeft: 10 }}>
            <Button
              variant="contained"
              onClick={() => {
                setListPimParams({ ...listPimParams });
              }}
            >
              {t("mobile.refresh")}
            </Button>
          </div>
        </div>
        <div style={flex.flexAuto}>
          {retryQueue.length > 0 && (
            <div style={{ marginLeft: 10 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  retryTransactions();
                }}
              >
                {t("mobile.retryTransactions", { length: retryQueue.length })}
              </Button>
            </div>
          )}
        </div>
      </div>
      {props.bookingId == null && (

        <div style={flex.flexRows}>
          <div style={flex.flexFill}>
          </div>
          <div style={flex.flexAuto}>
            <div style={{ padding: 5 }}>
              <DateField label={t("booking.labels.startDate")} value={startDate} onChange={(date) => {
                setStartDate(date);
                setListPimParams({ ...listPimParams, startDate: date });
              }} />
            </div>
          </div>
          <div style={flex.flexAuto}>
            <div style={{ padding: 5 }}>
              <TimeField fiveMinutes={false} label={t("kmos.labels.startTime")} value={startDate} onChange={(date) => {
                setStartDate(date);
                setListPimParams({ ...listPimParams, startDate: date });
              }} />
            </div>

          </div>
          <div style={flex.flexAuto}>
            <div style={{ padding: 5 }}>
              <DateField label={t("booking.labels.endDate")} value={endDate} onChange={(date) => {
                setEndDate(date);
                setListPimParams({ ...listPimParams, endDate: date });
              }} />
            </div>
          </div>
          <div style={flex.flexAuto}>
            <div style={{ padding: 5 }}>
              <TimeField fiveMinutes={false} label={t("kmos.labels.endTime")} value={endDate} onChange={(date) => {
                setEndDate(date);
                setListPimParams({ ...listPimParams, endDate: date });
              }} />
            </div>
          </div>


        </div>


      )}
      <div style={{ minHeight: 300 }}>
        <PagedDataView<IListPim, PimTransaction>
          apiHook={api.hooks.admin.listPimTransactions}
          parameters={listPimParams}
          renderItem={(item) => {
            const retryIndex = retryQueue.findIndex((tran) => {
              return tran.pimId == item.id;
            });
            const isSelectedForRetry = retryIndex != -1;

            return (
              <PimTranListItem
                selectedForRetry={isSelectedForRetry}
                pim={item}
                onClick={() => {
                  console.log(item);
                }}
                onRetry={() => {
                  if (isSelectedForRetry == true) {
                    const queue = [...retryQueue];
                    queue.splice(retryIndex, 1);
                    setRetryQueue(queue);
                    return;
                  }

                  setRetryQueue([
                    ...retryQueue,
                    ...[
                      { pimId: item.id },
                    ],
                  ]);
                }}
              />
            );
          }}
        >
          <div style={{ padding: 20, backgroundColor: "#eee" }}>
            {t("mobile.noTransactionsFound")}
          </div>
        </PagedDataView>
      </div>
    </Fragment>
  );
};

PimTransactionsView.propTypes = {
  clientCode: propTypes.string,
  bookingId: propTypes.number
};
export default PimTransactionsView;
